import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Mail, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { FadeUp } from "@/components/Reveal";

const inputCls = "w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-accent transition-colors";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — we'll be in touch.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="pt-32 md:pt-40 pb-24 min-h-screen" data-testid="contact-page">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-5">
            <FadeUp>
              <p className="label text-accent mb-6">/ Contact</p>
              <h1 className="font-display text-5xl md:text-7xl font-light leading-[0.9] tracking-tight">
                Talk to the lab.
              </h1>
              <p className="mt-8 text-sm text-muted-foreground max-w-md leading-relaxed">
                Specs, bulk orders, documentation, or partnerships — send a note and a real person will reply.
              </p>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div className="mt-12 space-y-5 text-sm">
                <div className="flex items-center gap-4"><Mail size={18} strokeWidth={1.5} className="text-accent" /> coastchems@gmail.com</div>
                <div className="flex items-center gap-4"><MapPin size={18} strokeWidth={1.5} className="text-accent" /> Pacific Coast Lab</div>
                <div className="flex items-center gap-4"><Clock size={18} strokeWidth={1.5} className="text-accent" /> Mon–Fri · 9–5 PT</div>
              </div>
            </FadeUp>
          </div>

          <div className="md:col-span-7">
            {sent ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border border-border p-12" data-testid="contact-success">
                <div className="h-16 w-16 flex items-center justify-center bg-accent text-accent-foreground"><Check size={32} strokeWidth={1.5} /></div>
                <h2 className="mt-8 font-display text-4xl font-light">Message received.</h2>
                <p className="mt-4 text-sm text-muted-foreground max-w-md">Thanks, {form.name.split(" ")[0]}. We will reply to {form.email} shortly.</p>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="border border-border p-8 md:p-10 grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="contact-form">
                <div>
                  <label className="label text-muted-foreground block mb-2">Name</label>
                  <input name="name" required value={form.name} onChange={onChange} className={inputCls} data-testid="contact-name" />
                </div>
                <div>
                  <label className="label text-muted-foreground block mb-2">Email</label>
                  <input name="email" type="email" required value={form.email} onChange={onChange} className={inputCls} data-testid="contact-email" />
                </div>
                <div className="sm:col-span-2">
                  <label className="label text-muted-foreground block mb-2">Subject</label>
                  <input name="subject" value={form.subject} onChange={onChange} className={inputCls} data-testid="contact-subject" />
                </div>
                <div className="sm:col-span-2">
                  <label className="label text-muted-foreground block mb-2">Message</label>
                  <textarea name="message" required rows={6} value={form.message} onChange={onChange} className={`${inputCls} resize-none`} data-testid="contact-message" />
                </div>
                <div className="sm:col-span-2">
                  <button type="submit" disabled={busy} className="w-full h-14 bg-primary text-primary-foreground label hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50" data-testid="contact-submit">
                    {busy ? "Sending…" : "Send message"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
